import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, FlaskConical, ImagePlus, X, CalendarClock } from "lucide-react";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { NewsletterTemplates } from "./NewsletterTemplates";
import { ListingsBlockControl } from "./ListingsBlockControl";

const EMPTY = { subject: "", subject_fr: "", message: "", message_fr: "", cta_label: "", cta_label_fr: "", cta_url: "", image: "",
  listings_enabled: false, listings_count: 6, listings_categories: [] };

const F = ({ label, k, f, set, area }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    {area
      ? <Textarea rows={5} value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-xl" data-testid={`nl-${k}`} />
      : <Input value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-xl" data-testid={`nl-${k}`} />}
  </div>
);

const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const STATUS = (n) => ({
  sent: `${n.emailed}/${n.recipients} delivered`, sending: "Sending…", cancelled: "Cancelled",
  scheduled: `Scheduled for ${n.scheduled_at ? fmt(n.scheduled_at) : ""}`,
}[n.status] || n.status);

const History = ({ items, onCancel }) => !items.length ? null : (
  <div className="mt-6" data-testid="nl-history">
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Newsletters</p>
    <ul className="divide-y divide-slate-100">
      {items.map((n) => (
        <li key={n.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`nl-history-${n.id}`}>
          <span className="font-medium text-[#12234A] truncate">{n.subject}</span>
          <span className="flex items-center gap-2 shrink-0">
            <span className={n.status === "scheduled" ? "text-amber-700 font-semibold" : "text-slate-500"} data-testid={`nl-status-${n.id}`}>
              {n.status === "scheduled" && <CalendarClock size={13} className="inline mr-1 -mt-0.5" />}{STATUS(n)} · {n.created_at?.slice(0, 10)}
            </span>
            {n.status === "scheduled" && (
              <button onClick={() => onCancel(n)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`nl-cancel-${n.id}`}>Cancel</button>
            )}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const SendLater = ({ later, setLater, at, setAt }) => (
  <div className="mt-4 rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3" data-testid="nl-schedule">
    <label className="flex items-center gap-2 text-sm font-semibold text-[#12234A] cursor-pointer">
      <Switch checked={later} onCheckedChange={setLater} data-testid="nl-send-later" /> Send later
    </label>
    {later && (
      <>
        <Input type="datetime-local" value={at} min={toLocalInput(new Date())} onChange={(e) => setAt(e.target.value)} className="h-9 rounded-lg w-56" data-testid="nl-send-at" />
        <span className="text-xs text-slate-500">Your time zone. Goes out within 15 minutes of this time.</span>
      </>
    )}
  </div>
);

export function NewsletterComposer({ count }) {
  const [f, setF] = useState(EMPTY);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [hist, setHist] = useState([]);
  const [later, setLater] = useState(false);
  const [at, setAt] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const loadHist = () => api.get("/admin/newsletter").then((r) => setHist(r.data.items)).catch(() => {});
  useEffect(() => { loadHist(); }, []);

  const run = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); } };
  const upload = (e) => run("img", async () => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    set("image", data.path);
  });
  const test = () => run("test", async () => { await api.post("/admin/newsletter/test", { ...f, to: testTo }); toast.success(`Test sent to ${testTo}`); });
  const send = () => run("send", async () => {
    const when = later && at ? new Date(at) : null;
    if (later && !when) { toast.error("Pick a date and time"); return; }
    if (when && when <= new Date()) { toast.error("Pick a time in the future"); return; }
    const q = when ? `Schedule “${f.subject}” for ${fmt(when)}? It goes to everyone subscribed at that time (${count} now).`
      : `Send “${f.subject}” to ${count} newsletter subscriber${count === 1 ? "" : "s"}?`;
    if (!window.confirm(q)) return;
    const { data } = await api.post("/admin/newsletter", { ...f, scheduled_at: when ? when.toISOString() : "" });
    toast.success(when ? `Scheduled for ${fmt(data.scheduled_at)}` : `Sending to ${data.recipients} subscriber${data.recipients === 1 ? "" : "s"}`);
    setF(EMPTY);
    setLater(false);
    setAt("");
    setTimeout(loadHist, 300);
    if (!when) setTimeout(loadHist, 5000);
  });
  const cancel = (n) => run("cancel", async () => {
    if (!window.confirm(`Cancel “${n.subject}”? It won't be sent.`)) return;
    await api.delete(`/admin/newsletter/${n.id}`);
    toast.success("Scheduled newsletter cancelled");
    loadHist();
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="newsletter-composer">
      <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><Send size={16} className="text-[#F5551E]" /> Send a newsletter</h3>
      <p className="text-xs text-slate-500 mt-1 mb-4">Goes to everyone who ticked “Newsletter” ({count}). French subscribers get the French wording when filled in. Every email has an unsubscribe link.</p>
      <NewsletterTemplates f={f} setF={setF} empty={EMPTY} />
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Subject" k="subject" f={f} set={set} />
        <F label="Subject (French)" k="subject_fr" f={f} set={set} />
        <F label="Message" k="message" f={f} set={set} area />
        <F label="Message (French)" k="message_fr" f={f} set={set} area />
        <F label="Button text (optional)" k="cta_label" f={f} set={set} />
        <F label="Button text (French)" k="cta_label_fr" f={f} set={set} />
      </div>
      <div className="mt-3"><F label="Button link — /browse or https://…" k="cta_url" f={f} set={set} /></div>
      <div className="mt-3 flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline" data-testid="nl-image-upload">
          {busy === "img" ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {f.image ? "Replace image" : "Add image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        {f.image && <><img src={photoUrl(f.image)} alt="" className="h-10 w-16 object-cover rounded" /><button onClick={() => set("image", "")} data-testid="nl-image-remove" className="text-slate-400 hover:text-red-600"><X size={15} /></button></>}
      </div>
      <ListingsBlockControl f={f} set={set} />
      <SendLater later={later} setLater={setLater} at={at} setAt={setAt} />
      <div className="mt-5 flex flex-wrap items-end gap-2">
        <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="h-10 rounded-xl w-60" data-testid="nl-test-to" />
        <Button variant="outline" onClick={test} disabled={!!busy || !testTo || !f.subject || !f.message} className="rounded-xl gap-1.5" data-testid="nl-test-send">
          {busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} Send test
        </Button>
        <Button onClick={send} disabled={!!busy || (!count && !later) || !f.subject || !f.message || (later && !at)} className="rounded-xl gap-1.5 bg-[#F5551E] hover:bg-[#e04812] ml-auto" data-testid="nl-send">
          {busy === "send" ? <Loader2 size={15} className="animate-spin" /> : later ? <CalendarClock size={15} /> : <Send size={15} />} {later ? "Schedule" : `Send to ${count}`}
        </Button>
      </div>
      <History items={hist} onCancel={cancel} />
    </div>
  );
}
